import { localize } from '../i18n';
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import NotificationsOffOutlinedIcon from '@mui/icons-material/NotificationsOffOutlined';
import PermissionNotice from './PermissionNotice';
import WebviewMessenger from '../utils/WebviewMessenger';

export default function NotificationPermissionBanner({ needed = true, channelIds = ['default'] }) {
    const [permission, setPermission] = useState(null);
    // Persisted capability flags describe a previous launch, not this WebView document.
    const [runtime, setRuntime] = useState(() => window.__APP_RUNTIME_INFO || null);
    const lastCapability = useRef(runtime?.capabilities?.notificationPermissionStatus);
    const pending = useRef(null);
    const timer = useRef(null);
    const sequence = useRef(0);
    const instance = useId().replace(/:/g, '_');
    const refresh = useCallback(() => {
        window.clearTimeout(timer.current);
        const requestId = `notification_${instance}_${++sequence.current}`;
        pending.current = requestId;
        timer.current = window.setTimeout(() => {
            if (pending.current === requestId) {
                pending.current = null;
                setPermission({ error: true, timeout: true });
            }
        }, 4000);
        WebviewMessenger.send({ type: 'GET_NOTIFICATION_PERMISSION', requestId });
    }, [instance]);
    const recheck = useCallback(() => {
        WebviewMessenger.send({ type: 'REQUEST_APP_RUNTIME_INFO' });
        refresh();
    }, [refresh]);

    useEffect(() => {
        if (!needed || !window.ReactNativeWebView) return undefined;
        // This read-only message is harmless on old wrappers (ignored). A correlated
        // permission ACK, not an OTA-only flag or store version, establishes support.
        const unsubscribe = WebviewMessenger.onMessage(message => {
            if (message.type !== 'NOTIFICATION_PERMISSION_STATUS' || !pending.current || message.requestId !== pending.current) return;
            window.clearTimeout(timer.current);
            pending.current = null;
            const data = message.data;
            setPermission(message.error || !data || typeof data.granted !== 'boolean'
                || !['granted', 'denied', 'undetermined'].includes(data.status) ? { error: true } : data);
        });
        const onRuntime = event => {
            const next = event.detail || window.__APP_RUNTIME_INFO || null;
            const capability = next?.capabilities?.notificationPermissionStatus;
            const changed = capability !== lastCapability.current;
            lastCapability.current = capability;
            setRuntime(next);
            // Other permission cards also poll runtime. Unchanged capabilities do
            // not invalidate a settled permission or restart its read deadline.
            // Actual foreground and denied/unknown polling refresh separately.
            if (changed) refresh();
        };
        const onVisible = () => { if (document.visibilityState === 'visible') recheck(); };
        window.addEventListener('focus', recheck);
        window.addEventListener('app-runtime-info-updated', onRuntime);
        window.addEventListener('native-app-foreground', refresh);
        document.addEventListener('visibilitychange', onVisible);
        refresh();
        WebviewMessenger.send({ type: 'REQUEST_APP_RUNTIME_INFO' });
        return () => {
            pending.current = null;
            window.clearTimeout(timer.current);
            unsubscribe();
            window.removeEventListener('focus', recheck);
            window.removeEventListener('app-runtime-info-updated', onRuntime);
            window.removeEventListener('native-app-foreground', refresh);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [needed, refresh, recheck]);

    const quiet = permission?.iosStatus === 3;
    const alertDisabled = permission?.allowsAlert === false;
    const soundDisabled = permission?.allowsSound === false;
    const channelIssues = (Array.isArray(permission?.androidChannels) ? permission.androidChannels : [])
        .filter(channel => channelIds.includes(channel.id)).flatMap(channel => {
            const names = { default: localize('일반 알림'), 'pill-reminder-alarm': localize('벨소리 알림'),
                'pill-reminder-vibration': localize('진동 알림'), 'pill-alarm-foreground-v2': localize('알람 화면') };
            const name = names[channel.id] || channel.name || localize('알림');
            if (channel.importance === 0) return [{ title: localize("{v0} 표시 꺼짐", { v0: name }), description: localize('알림 카테고리를 허용해 주세요.') }];
            if (typeof channel.importance === 'number' && channel.importance < 4) return [{ title: localize("{v0} 표시 제한", { v0: name }), description: localize('알림 카테고리의 표시를 확인해 주세요.') }];
            if (['default', 'pill-reminder-alarm'].includes(channel.id) && channel.sound === null) {
                return [{ title: localize("{v0} 소리 꺼짐", { v0: name }), description: localize('알림 카테고리의 소리를 켜 주세요.') }];
            }
            if (channel.id === 'pill-reminder-vibration' && channel.enableVibrate === false) {
                return [{ title: localize('진동 알림 진동 꺼짐'), description: localize('알림 카테고리의 진동을 켜 주세요.') }];
            }
            return [];
        });
    const needsHelp = permission && (permission.error || !permission.granted || quiet || alertDisabled || soundDisabled
        || permission.channelReadError || channelIssues.length > 0);
    const permissionStatusSupported = runtime?.capabilities?.notificationPermissionStatus === true;
    useEffect(() => {
        if (!needed || !needsHelp || (permission.error && !permissionStatusSupported)) return undefined;
        // Settings return does not always trigger WebView focus. Only actual OS reads resolve this notice.
        const interval = window.setInterval(() => {
            if (document.visibilityState === 'visible' && !pending.current) refresh();
        }, 1500);
        return () => window.clearInterval(interval);
    }, [needed, needsHelp, permission?.error, permissionStatusSupported, refresh]);
    if (!needed || !window.ReactNativeWebView) return null;
    if (!needsHelp) return null;
    if (permission.granted && !alertDisabled && !soundDisabled && !quiet && (permission.channelReadError || channelIssues.length)) {
        const notice = permission.channelReadError ? { title: localize('알림 카테고리 확인 필요'), description: localize('설정을 확인하지 못했어요.') }
            : channelIssues.length === 1 ? channelIssues[0]
                : { title: localize("알림 카테고리 {v0}개 확인 필요", { v0: channelIssues.length }), description: localize('카테고리별 표시·소리·진동을 확인해 주세요.') };
        return <PermissionNotice icon={<NotificationsOffOutlinedIcon />} {...notice}
            actionLabel={permission.channelReadError ? localize('다시 확인') : localize('설정 열기')}
            onAction={() => permission.channelReadError ? refresh() : WebviewMessenger.send({ type: 'OPEN_NOTIFICATION_SETTINGS' })} />;
    }

    const title = permission.error ? localize('알림 권한 확인 필요')
        : quiet ? localize('일반 알림 소리가 안 나요')
        : !permission.granted ? localize('알림 권한이 필요해요')
        : alertDisabled && soundDisabled ? localize('일반 알림 배너·소리 꺼짐')
        : alertDisabled ? localize('일반 알림 배너가 꺼졌어요')
        : soundDisabled ? localize('일반 알림 소리가 꺼졌어요')
        : localize('알림 권한이 필요해요');
    const description = permission.error ? (permission.timeout && !permissionStatusSupported
        ? localize('앱을 완전히 종료한 뒤 다시 열어 주세요.') : localize('권한을 확인하지 못했어요.'))
        : quiet ? localize('알림 센터에서만 보여요.')
        : !permission.granted ? localize('앱 알림을 허용해 주세요.')
        : alertDisabled && soundDisabled ? localize('배너와 소리를 켜 주세요.')
        : alertDisabled ? localize('설정에서 배너를 켜 주세요.')
        : localize('설정에서 소리를 켜 주세요.');
    const openSettings = permission.granted && (alertDisabled || soundDisabled);
    return (
        <PermissionNotice
            icon={<NotificationsOffOutlinedIcon />}
            title={title}
            description={description}
            actionLabel={permission.error ? localize('다시 확인') : openSettings ? localize('설정 열기') : localize('권한 허용')}
            onAction={() => {
                if (permission.error) recheck();
                else WebviewMessenger.send({ type: openSettings ? 'OPEN_NOTIFICATION_SETTINGS' : 'REQUEST_NOTIFICATION_PERMISSION' });
            }}
        />
    );
}
